import {
  renderFriendlyArithmetic,
  renderFriendlyFunction,
} from 'app/services/formula/friendlyFunctionReadback';
import { READBACK_GLOSSES } from 'app/services/formula/operatorGlosses';
import LexicalParser from 'app/services/formula/parser/lexicalParser';
import SyntaxParser, { syntaxParser } from 'app/services/formula/parser/syntaxParser';
import { resolveLiteralLabel, resolveOperandFieldPath } from 'app/services/formula/valueLabels';
import _ from 'lodash';

import { LEX_TOKEN_TYPES } from 'constants/parser';

// Constants

const MAX_READBACK_LENGTH = 300;

const EMPTY_READBACK_CONTEXT = { valueLabels: null, iteratorPaths: {} };

const DEFAULT_ITERATOR_NAME = '_';

const DATE_READBACK_GLOSSES = {
  '==': 'is on',
  '!=': 'is not on',
  '>': 'is after',
  '<': 'is before',
  '>=': 'is on or after',
  '<=': 'is on or before',
};

const DATE_FUNCTION_NAMES = new Set(['DATE', 'DATE_NOW']);

// A comparison operand reads as a date when it is a date-typed field or a
// date-producing function (DATE('…') / DATE_NOW()).
const isDateOperand = node => {
  if (_.isNil(node)) {
    return false;
  }

  if (node.type === LEX_TOKEN_TYPES.function) {
    return DATE_FUNCTION_NAMES.has(String(node.value).toUpperCase());
  }

  return node.typeOfValue === LEX_TOKEN_TYPES.date || node.typeOfValue === 'date';
};

const ARITHMETIC_OPERATORS = new Set(['+', '-', '*', '/']);
const ARITHMETIC_PRECEDENCE = { '+': 1, '-': 1, '*': 2, '/': 2 };

// A child arithmetic operand of a different precedence level must be
// parenthesized in the readback, else "100 plus 50 times 10" is ambiguous (it
// is 100 + (50 * 10)). Same-precedence chains stay flat — they read left-to-right.
const parenthesizeArithmeticOperand = (operandNode, operandText, parentOp) => {
  const childOp = operandNode?.value;
  const isArithmeticChild =
    operandNode?.type === LEX_TOKEN_TYPES.operator && ARITHMETIC_OPERATORS.has(childOp);

  if (!isArithmeticChild || ARITHMETIC_PRECEDENCE[childOp] === ARITHMETIC_PRECEDENCE[parentOp]) {
    return operandText;
  }

  return `(${operandText})`;
};

const LITERAL_COMPARISON_GLOSSES = {
  '==': 'equals',
  '!=': 'does not equal',
  '>': 'is greater than',
  '<': 'is less than',
  '>=': 'is at least',
  '<=': 'is at most',
};

const AGGREGATE_PHRASES = {
  COUNT: 'the number of',
  SUM: 'the total of',
  MAX_BY: 'the maximum of',
  MIN_BY: 'the minimum of',
  WHERE: 'the items from',
};

const LITERAL_NODE_TYPES = new Set([
  LEX_TOKEN_TYPES.value,
  LEX_TOKEN_TYPES.bool,
  LEX_TOKEN_TYPES.decimal,
]);

// prettifyName — duplicated from suggestionEngine so this module is standalone

/**
 * Split a camelCase/PascalCase segment into spaced Title Case words.
 * Underscore-prefixed names (iterator variables) are returned unchanged.
 *
 * @param {string} segment
 * @returns {string}
 */
const prettifySegment = segment => {
  if (!segment || segment.startsWith('_')) {
    return segment;
  }

  const spaced = segment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Prettify the last dot-separated segment of a field path.
 * For iterator-rooted paths like '_x.Foo', return "the item's Foo".
 *
 * @param {string} fieldPath
 * @returns {string}
 */
const prettifyFieldPath = fieldPath => {
  if (!fieldPath) {
    return fieldPath;
  }

  const segments = fieldPath.split('.');
  const last = segments[segments.length - 1];

  if (segments[0] && segments[0].startsWith('_')) {
    return `the item's ${prettifySegment(last)}`;
  }

  return prettifySegment(last);
};

// AST walker

/**
 * Truncate a string to MAX_READBACK_LENGTH, appending ellipsis when needed.
 *
 * @param {string} text
 * @returns {string}
 */
const truncate = text =>
  text.length > MAX_READBACK_LENGTH ? `${text.slice(0, MAX_READBACK_LENGTH)}…` : text;

/**
 * Walk an AST node and return a plain-English phrase for it.
 * Falls back to the node's raw `value` for any unrecognised shape so output
 * never crashes or returns null.
 *
 * @param {Object|null} node
 * @param {{ valueLabels: Object|null, iteratorPaths: Object }} ctx label lookup + iterator bindings
 * @returns {string}
 */
const walkNode = (node, ctx = EMPTY_READBACK_CONTEXT) => {
  if (_.isNil(node)) {
    return '';
  }

  const { type, value, typeOfValue, operands = [] } = node;

  // Leaf: field path
  if (type === LEX_TOKEN_TYPES.field) {
    return prettifyFieldPath(value);
  }

  // Leaf: literal value
  if (type === LEX_TOKEN_TYPES.value) {
    if (typeOfValue === 'string') {
      return value;
    }

    return String(value);
  }

  // Operator node (binary infix) — date comparisons read with date phrasing
  if (type === LEX_TOKEN_TYPES.operator) {
    const isDateComparison = operands.some(isDateOperand);
    const bothLiterals =
      !isDateComparison &&
      operands.length === 2 &&
      LITERAL_NODE_TYPES.has(operands[0]?.type) &&
      LITERAL_NODE_TYPES.has(operands[1]?.type);

    const resolveGlossMap = () => {
      if (isDateComparison) {
        return DATE_READBACK_GLOSSES;
      }

      if (bothLiterals) {
        return { ...READBACK_GLOSSES, ...LITERAL_COMPARISON_GLOSSES };
      }

      return READBACK_GLOSSES;
    };

    const labelLiteralOperand = (literalNode, otherNode, fallbackText) => {
      if (literalNode?.type !== LEX_TOKEN_TYPES.value) {
        return fallbackText;
      }

      const comparedPath = resolveOperandFieldPath(otherNode, ctx.iteratorPaths);
      const label = resolveLiteralLabel(ctx.valueLabels, comparedPath, literalNode.value);

      return label ?? fallbackText;
    };

    const left = labelLiteralOperand(operands[0], operands[1], walkNode(operands[0], ctx));
    const right = labelLiteralOperand(operands[1], operands[0], walkNode(operands[1], ctx));

    if (ARITHMETIC_OPERATORS.has(value)) {
      return renderFriendlyArithmetic({
        op: value,
        leftText: parenthesizeArithmeticOperand(operands[0], left, value),
        rightText: parenthesizeArithmeticOperand(operands[1], right, value),
      }).trim();
    }

    const glossMap = resolveGlossMap();
    const gloss = glossMap[value] ?? READBACK_GLOSSES[value] ?? value;

    return `${left} ${gloss} ${right}`.trim();
  }

  // Function nodes
  if (type === LEX_TOKEN_TYPES.function) {
    return walkFunction(value, operands, ctx);
  }

  // Fallback: return raw value
  return String(value ?? '');
};

/**
 * Render a function call node to English.
 *
 * @param {string} funcName
 * @param {Array} operands
 * @param {{ valueLabels: Object|null, iteratorPaths: Object }} ctx label lookup + iterator bindings
 * @returns {string}
 */
const walkFunction = (funcName, operands, ctx = EMPTY_READBACK_CONTEXT) => {
  const upper = (funcName ?? '').toUpperCase();

  // Logical connectives
  if (upper === 'AND') {
    return operands.map(node => walkNode(node, ctx)).join(', AND ');
  }

  if (upper === 'OR') {
    return operands.map(node => walkNode(node, ctx)).join(', OR ');
  }

  if (upper === 'NOT') {
    return `NOT (${walkNode(operands[0], ctx)})`;
  }

  if (upper === 'ANY' || upper === 'ALL') {
    const arrayNode = operands[0];

    if (arrayNode?.type === LEX_TOKEN_TYPES.field) {
      const qualifier = upper === 'ANY' ? 'any' : 'every';
      const predNode = operands[operands.length - 1];
      const collectionLabel = prettifySegment((arrayNode.value ?? '').split('.').pop());
      // The parser may inject its own iterator token whose name is derived from
      // the array token, while the predicate still refers to the default `_`.
      // Bind both so either spelling resolves to the array path.
      const iteratorNode = operands.length > 2 ? operands[1] : null;
      const iteratorNames = _.uniq([DEFAULT_ITERATOR_NAME, iteratorNode?.value].filter(_.isString));
      const predicateCtx = {
        ...ctx,
        iteratorPaths: {
          ...ctx.iteratorPaths,
          ...Object.fromEntries(iteratorNames.map(name => [name, arrayNode.value])),
        },
      };

      return `${qualifier} ${collectionLabel} has: ${walkNode(predNode, predicateCtx)}`;
    }
  }

  if (upper === 'WHERE' && operands[0]?.type !== LEX_TOKEN_TYPES.field) {
    const prettifiedName = prettifySegment(funcName);
    const argTexts = operands.map(node => walkNode(node, ctx)).join(', ');

    return argTexts ? `${prettifiedName} of ${argTexts}` : prettifiedName;
  }

  // Aggregate functions
  if (AGGREGATE_PHRASES[upper]) {
    const phrase = AGGREGATE_PHRASES[upper];
    const arrayNode = operands[0];
    const predNode = operands[operands.length - 1];
    const collectionLabel = arrayNode
      ? prettifySegment((arrayNode.value ?? '').split('.').pop())
      : '…';

    if (operands.length > 1) {
      return `${phrase} ${collectionLabel} where ${walkNode(predNode, ctx)}`;
    }

    return `${phrase} ${collectionLabel}`;
  }

  // SWITCH / CASE
  if (upper === 'SWITCH') {
    const caseTexts = operands.map(node => walkNode(node, ctx)).join('; ');

    return `first match: ${caseTexts}`;
  }

  if (upper === 'CASE') {
    const condition = walkNode(operands[0], ctx);
    const result = walkNode(operands[1], ctx);

    return `when ${condition} then ${result}`;
  }

  // Date helpers

  // DATE('YYYY-MM-DD', 'UTC') — date literal constructor: render just the date
  if (upper === 'DATE' && operands.length >= 1) {
    const innerArg = operands[0];
    const rawValue = String(innerArg?.value ?? '').replace(/^'|'$/g, '');

    return rawValue;
  }

  // Friendly function/arithmetic templates shared with the visual builder readback
  const friendly = renderFriendlyFunction({
    fnName: funcName,
    argTexts: operands.map(node => walkNode(node, ctx)),
  });

  if (!_.isNil(friendly)) {
    return friendly;
  }

  // STRING_JOIN(arr, sep?) -> "joined <collection>" or "joined <collection> with <sep>"
  if (upper === 'STRING_JOIN') {
    const arrayNode = operands[0];
    const collectionLabel = arrayNode
      ? prettifySegment((arrayNode.value ?? '').split('.').pop())
      : '…';

    if (operands.length > 1) {
      return `joined ${collectionLabel} with ${walkNode(operands[1], ctx)}`;
    }

    return `joined ${collectionLabel}`;
  }

  // Generic function fallback — friendlier than raw FUNCNAME(args)
  const prettifiedName = prettifySegment(funcName);
  const argTexts = operands.map(node => walkNode(node, ctx)).join(', ');

  return argTexts ? `${prettifiedName} of ${argTexts}` : prettifiedName;
};

// Public API

/**
 * Parse expressionText and return a plain-English sentence describing what it
 * evaluates to.  Returns null when the expression is empty, unparseable, or
 * if any error is thrown during parsing or walking.
 *
 * @param {{ expressionText: string, schemaParams: Object, isNewExpressionsParsingLogicEnabled: boolean }} params
 * @returns {string|null}
 */
const parseToAst = ({ tokens, schemaParams, isNewExpressionsParsingLogicEnabled }) => {
  if (isNewExpressionsParsingLogicEnabled) {
    const newSyntaxParser = new SyntaxParser(schemaParams);
    const { data } = newSyntaxParser.parse({ tokens });

    return data;
  }

  const { data } = syntaxParser({ tokens, schemaParams });

  return data;
};

const getExpressionReadback = ({
  expressionText,
  schemaParams,
  isNewExpressionsParsingLogicEnabled,
}) => {
  if (!expressionText || !expressionText.trim() || _.isNil(schemaParams?.entities)) {
    return null;
  }

  try {
    const lexicalParser = new LexicalParser(schemaParams);
    const tokens = lexicalParser.parse(expressionText);

    if (!tokens || !tokens.length) {
      return null;
    }

    const data = parseToAst({ tokens, schemaParams, isNewExpressionsParsingLogicEnabled });

    if (_.isNil(data)) {
      return null;
    }

    const sentence = walkNode(data, {
      ...EMPTY_READBACK_CONTEXT,
      valueLabels: schemaParams?.valueLabels ?? null,
    });

    if (!sentence || !sentence.trim()) {
      return null;
    }

    return truncate(sentence);
  } catch {
    return null;
  }
};

export default getExpressionReadback;
